import { useMemo, useRef, useState, type DragEvent } from 'react';
import { Link } from 'react-router-dom';
import { formatDate, istanbulDayString, t } from '@aquabox/shared';
import {
  useBulkAssign,
  useMe,
  useOrderMutations,
  useOrders,
  useRegions,
  useStaleOpenTrips,
  useVehicles,
} from '../api/queries';
import { StaleBadge } from '../components/StaleBadge';
import type { BulkAssignResult } from '../api/queries';
import { skipReasonKey } from '../components/BulkAssignDialog';
import { CustomerPicker } from '../components/CustomerPicker';
import { ChannelPill, RegionMatchBadge } from '../components/Pills';
import { useToast } from '../components/Toast';
import { useShadowAssign } from '../api/shadow-assign';
import { rateText } from './ShadowAssignCard';
import type { Order, Vehicle } from '../api/types';

/** Atanmamış sütunun anahtarı; araç id'leriyle çakışmayan sabit bir değer. */
const UNASSIGNED = '__unassigned__';
/** Pasif ya da bilinmeyen araca bağlı açık siparişler; yalnız varsa görünür, bırakma hedefi değil. */
const OTHER = '__other__';

/** Açık bekleyenler (tarih filtresi yok: dünden kalan araçsız sipariş de görünür). */
const PENDING_STATUSES = 'NEW,CONFIRMED';
/** Araca atanmış açık siparişler; ON_ROUTE olanlar sürüklenemez. */
const ASSIGNED_STATUSES = 'ASSIGNED,ON_ROUTE';

function movable(order: Order | undefined): boolean {
  return order !== undefined && order.status !== 'ON_ROUTE';
}

/** Panel `Order` tipinde yok; sipariş listesi yanıtı `requestedDeliveryAt` taşır (orders.service select). */
function requestedDeliveryAt(o: Order): string | null {
  return (o as Order & { requestedDeliveryAt?: string | null }).requestedDeliveryAt ?? null;
}

/** İleri tarihli teslim için kısa "GG.AA" rozeti; bugün ya da geçmiş tarih rozet almaz. */
function dueBadge(o: Order, today: string): { short: string; full: string } | null {
  const raw = requestedDeliveryAt(o);
  if (!raw) return null;
  const date = new Date(raw);
  if (istanbulDayString(date) <= today) return null;
  return { short: formatDate(date).slice(0, 5), full: formatDate(date) };
}

function LockIcon() {
  return (
    <span
      className="day-board-lock"
      role="img"
      aria-label={t('dayBoard.onRoute')}
      title={t('dayBoard.onRoute')}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        aria-hidden="true"
      >
        <rect x="4" y="9" width="12" height="8" rx="1.5" />
        <path d="M7 9V6.5a3 3 0 0 1 6 0V9" />
      </svg>
    </span>
  );
}

/** Elle girilmiş ama müşterisiz açık sipariş: şoför veresiye/zimmet yazamaz, müşteri bağlanmalı. */
function needsCustomer(o: Order): boolean {
  return o.source === 'MANUAL' && o.customer === null;
}

/** "Müşteri bağla": müşteriyi seç ya da satır içi oluştur → `POST /api/orders/:id/customer`. */
function LinkCustomerDialog({ order, onClose }: { order: Order; onClose: () => void }) {
  const { linkCustomer } = useOrderMutations();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('dayBoard.linkCustomerTitle')}
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="card modal-card" style={{ '--modal-w': '480px' } as React.CSSProperties}>
        <h2 style={{ fontWeight: 600, fontSize: 20 }}>{t('dayBoard.linkCustomerTitle')}</h2>
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: '6px 0 16px' }}>
          {order.customerName} · {t('dayBoard.linkCustomerHint')}
        </p>
        <CustomerPicker
          onSelect={(customer) => {
            setError(null);
            linkCustomer.mutate(
              { id: order.id, customerId: customer.id },
              {
                onSuccess: () => {
                  toast(t('dayBoard.linkCustomerDone'));
                  onClose();
                },
                onError: (err: unknown) => {
                  const message = (err as { message?: string } | null)?.message;
                  setError(message && message.length > 0 ? message : t('error.internal'));
                },
              },
            );
          }}
        />
        {error && (
          <div
            role="alert"
            style={{ color: 'var(--danger)', fontSize: 13.5, fontWeight: 500, marginTop: 10 }}
          >
            {error}
          </div>
        )}
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button
            type="button"
            className="btn"
            style={{ color: 'var(--text-secondary)' }}
            onClick={onClose}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Gün tahtası: siparişler araç sütunlarında. Atama yolu tek — `useBulkAssign` (sürükle-bırak,
 * "Taşı" menüsü ve gölge öneri rozeti aynı çağrıyı yapar). Veri mevcut sorgulardan gelir.
 */
export function DayBoard({ canWrite }: { canWrite: boolean }) {
  const pending = useOrders({ status: PENDING_STATUSES, pageSize: '100' });
  const assigned = useOrders({ status: ASSIGNED_STATUSES, pageSize: '100' });
  const vehicles = useVehicles();
  const regions = useRegions();
  const bulkAssign = useBulkAssign();
  const toast = useToast();
  // Bayat açık sefer: sütun başlığında rozet + Seferler ekranına bağlantı (kapatma orada).
  const staleTrips = useStaleOpenTrips();
  const staleByVehicle = useMemo(
    () => new Map(staleTrips.map((trip) => [trip.vehicle.id, trip] as const)),
    [staleTrips],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [linkOrder, setLinkOrder] = useState<Order | null>(null);
  // Sürüklenen id'ler: dragStart'ta yazılır, drop'ta okunur, dragEnd'de temizlenir.
  const dragIdsRef = useRef<string[] | null>(null);

  // Sütun başlığında aracın şoförünün 7 günlük panel uyumu (salt okunur, yalnız OWNER/MANAGER).
  // Oran sunucudan gelir (`byPerson[].d7.ratePct`); panel hesap yapmaz.
  const me = useMe();
  const canViewShadow = me.data?.role === 'OWNER' || me.data?.role === 'MANAGER';
  const shadow = useShadowAssign({ days: 30, poll: false, enabled: canViewShadow });
  const personRateByDriverId = new Map(
    (shadow.data?.byPerson ?? []).flatMap((p) =>
      p.driver ? [[p.driver.id, p.d7.ratePct] as const] : [],
    ),
  );

  const orders = useMemo(
    () => [...(pending.data?.items ?? []), ...(assigned.data?.items ?? [])],
    [pending.data, assigned.data],
  );
  const orderById = useMemo(() => new Map(orders.map((o) => [o.id, o] as const)), [orders]);
  const activeVehicles = useMemo(
    () => (vehicles.data?.items ?? []).filter((v: Vehicle) => v.isActive),
    [vehicles.data],
  );
  const vehicleById = useMemo(
    () => new Map(activeVehicles.map((v) => [v.id, v] as const)),
    [activeVehicles],
  );

  // Sütun → sipariş. Araçsız → Atanmamış; aktif araç → kendi sütunu; geri kalan (pasif/bilinmeyen
  // araç) → Diğer. Hiçbir sipariş sessizce düşmez.
  const columns = useMemo(() => {
    const map = new Map<string, Order[]>([
      [UNASSIGNED, []],
      [OTHER, []],
    ]);
    for (const v of activeVehicles) map.set(v.id, []);
    for (const o of orders) {
      const key = !o.vehicle ? UNASSIGNED : map.has(o.vehicle.id) ? o.vehicle.id : OTHER;
      map.get(key)?.push(o);
    }
    return map;
  }, [orders, activeVehicles]);

  const busy = bulkAssign.isPending;
  const visibleSelected = [...selected].filter((id) => orderById.has(id));

  // Her iki sorgu sayfa sınırında kesildiyse tahtada eksik sipariş var; uyarı gösterilir.
  const shownCount = orders.length;
  const totalCount = (pending.data?.total ?? 0) + (assigned.data?.total ?? 0);
  const truncated =
    (pending.data?.total ?? 0) > (pending.data?.items.length ?? 0) ||
    (assigned.data?.total ?? 0) > (assigned.data?.items.length ?? 0);

  /** Gölge öneri: sipariş yanıtındaki bölge aracı, yoksa bölge listesi (Orders.tsx ile aynı kaynak). */
  function suggestionFor(o: Order): Vehicle | undefined {
    if (!o.region) return undefined;
    const vehicleId =
      o.region.defaultVehicleId ??
      regions.data?.items.find((r) => r.id === o.region?.id)?.defaultVehicleId ??
      null;
    return vehicleId ? vehicleById.get(vehicleId) : undefined;
  }

  function notifyResult(res: BulkAssignResult): void {
    if (res.skipped.length === 0) {
      toast(t('panel.orders.bulkAssignAssigned', { n: res.assigned.length }));
      return;
    }
    const reasons = [...new Set(res.skipped.map((s) => t(skipReasonKey(s.reason))))].join(', ');
    toast(t('panel.orders.bulkAssignSkippedReason', { n: res.skipped.length, reasons }), 'danger');
  }

  function move(orderIds: string[], vehicleId: string): void {
    if (orderIds.length === 0) return;
    bulkAssign.mutate(
      { orderIds, vehicleId },
      {
        onSuccess: (res) => {
          setSelected((prev) => new Set([...prev].filter((id) => !orderIds.includes(id))));
          notifyResult(res);
        },
        onError: (err: unknown) => {
          const message = (err as { message?: string } | null)?.message;
          toast(
            t('panel.orders.bulkAssignError', {
              message: message && message.length > 0 ? message : t('error.internal'),
            }),
            'danger',
          );
        },
      },
    );
  }

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** Seçili kartın sürüklenmesi tüm (sürüklenebilir) seçimi taşır; diğerleri yalnız kendini. */
  function dragIdsFor(o: Order): string[] {
    if (!selected.has(o.id)) return [o.id];
    return visibleSelected.filter((id) => movable(orderById.get(id)));
  }

  function handleDragStart(e: DragEvent<HTMLLIElement>, o: Order): void {
    if (!canWrite || busy || !movable(o)) {
      e.preventDefault();
      return;
    }
    const ids = dragIdsFor(o);
    dragIdsRef.current = ids;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ids.join(','));
  }

  function handleDragOver(e: DragEvent<HTMLElement>): void {
    if (!canWrite || !dragIdsRef.current) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  function handleDrop(e: DragEvent<HTMLElement>, vehicleId: string): void {
    e.preventDefault();
    const ids = dragIdsRef.current;
    dragIdsRef.current = null;
    if (!ids || !canWrite) return;
    // Zaten bu araçtaki siparişler atlanır; geri kalanı mevcut bulk-assign ile gider.
    move(
      ids.filter((id) => orderById.get(id)?.vehicle?.id !== vehicleId),
      vehicleId,
    );
  }

  const renderCard = (o: Order) => {
    const locked = !movable(o);
    const itemCount = o.items.reduce((sum, item) => sum + item.qty, 0);
    const suggestion = o.vehicle ? undefined : suggestionFor(o);
    const due = dueBadge(o, istanbulDayString(new Date()));
    return (
      <li
        key={o.id}
        className={`day-board-card${locked ? ' is-locked' : ''}`}
        data-testid="day-board-card"
        data-order-id={o.id}
        draggable={canWrite && !locked && !busy}
        onDragStart={(e) => handleDragStart(e, o)}
        onDragEnd={() => {
          dragIdsRef.current = null;
        }}
      >
        <div className="day-board-card-top">
          {canWrite && (
            <input
              type="checkbox"
              checked={selected.has(o.id)}
              disabled={locked}
              aria-label={t('dayBoard.selectFor', { name: o.customerName })}
              onChange={() => toggle(o.id)}
            />
          )}
          <strong className="day-board-card-name">{o.customerName}</strong>
          {locked && <LockIcon />}
        </div>
        <div className="day-board-card-addr" title={o.address}>
          {o.address}
        </div>
        <div>
          <RegionMatchBadge order={o} />
        </div>
        <div className="day-board-card-meta">
          <span>{t('dayBoard.itemCount', { n: itemCount })}</span>
          <span className="day-board-card-badges">
            {due && (
              <span className="day-board-due" title={t('dayBoard.dueOn', { date: due.full })}>
                {due.short}
              </span>
            )}
            <ChannelPill channel={o.channel} source={o.source} />
          </span>
        </div>
        {canWrite && needsCustomer(o) && (
          <button
            type="button"
            className="day-board-suggest"
            data-testid="day-board-link-customer"
            aria-label={t('dayBoard.linkCustomerFor', { name: o.customerName })}
            onClick={() => setLinkOrder(o)}
          >
            {t('dayBoard.linkCustomer')}
          </button>
        )}
        {canWrite && suggestion && (
          <button
            type="button"
            className="day-board-suggest"
            disabled={locked || busy}
            onClick={() => move([o.id], suggestion.id)}
          >
            {t('dayBoard.suggestion', { plate: suggestion.plate })}
          </button>
        )}
        {canWrite && (
          <select
            className="input day-board-move"
            aria-label={t('dayBoard.moveFor', { name: o.customerName })}
            value=""
            disabled={locked || busy || activeVehicles.length === 0}
            onChange={(e) => {
              if (e.target.value) move([o.id], e.target.value);
            }}
          >
            <option value="">{t('panel.selectVehicle')}</option>
            {activeVehicles
              .filter((v) => v.id !== o.vehicle?.id)
              .map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plate}
                </option>
              ))}
          </select>
        )}
      </li>
    );
  };

  if (pending.isError || assigned.isError || vehicles.isError) {
    return (
      <p role="alert" style={{ padding: 32, color: 'var(--danger)' }}>
        {t('error.internal')}
      </p>
    );
  }
  if (pending.isPending || assigned.isPending || vehicles.isPending) {
    return <p style={{ padding: 32, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>;
  }

  const unassignedOrders = columns.get(UNASSIGNED) ?? [];
  const otherOrders = columns.get(OTHER) ?? [];

  return (
    <div className="day-board-screen">
      {linkOrder && <LinkCustomerDialog order={linkOrder} onClose={() => setLinkOrder(null)} />}
      <p className="day-board-hint">{t('dayBoard.hint')}</p>
      {truncated && (
        <Link to="/siparisler" className="day-board-truncated">
          {t('dayBoard.truncated', { shown: shownCount, total: totalCount })}
        </Link>
      )}
      {visibleSelected.length > 0 && (
        <p className="day-board-selection">
          {t('panel.orders.bulkAssignCount', { n: visibleSelected.length })}
        </p>
      )}
      {activeVehicles.length === 0 && <p className="day-board-hint">{t('dayBoard.noVehicles')}</p>}
      <div className="day-board" data-testid="day-board">
        <section
          className="day-board-col"
          aria-label={t('dayBoard.unassigned')}
          data-testid="day-board-col"
        >
          <header className="day-board-col-head">
            <span className="day-board-col-title">{t('dayBoard.unassigned')}</span>
            <span className="day-board-col-count">{unassignedOrders.length}</span>
          </header>
          <ul className="day-board-list">
            {unassignedOrders.length === 0 ? (
              <li className="day-board-empty">{t('dayBoard.noOrders')}</li>
            ) : (
              unassignedOrders.map(renderCard)
            )}
          </ul>
        </section>
        {activeVehicles.map((v) => {
          const cards = columns.get(v.id) ?? [];
          // Aracın şoför sırası panelde belirsizdir (sunucu "aktif ve tr sırasıyla ilk" seçer).
          // Oran, aracın TÜM şoförleri arasında `byPerson` sırasına göre ilk eşleşen kişiye aittir.
          const vehicleDrivers = v.drivers ?? [];
          const vehicleDriverIds = new Set(vehicleDrivers.map((d) => d.id));
          const person = (shadow.data?.byPerson ?? []).find(
            (p) => p.driver && vehicleDriverIds.has(p.driver.id),
          );
          const driverId = person?.driver?.id;
          const driverName =
            vehicleDrivers.find((d) => d.id === driverId)?.user.fullName ??
            person?.driver?.name ??
            vehicleDrivers[0]?.user.fullName;
          const driverRate = driverId ? personRateByDriverId.get(driverId) : undefined;
          return (
            <section
              key={v.id}
              className="day-board-col"
              aria-label={v.plate}
              data-testid="day-board-col"
              data-vehicle-id={v.id}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, v.id)}
            >
              <header className="day-board-col-head">
                <span className="day-board-col-title">{v.plate}</span>
                {staleByVehicle.get(v.id) && (
                  <Link
                    to={`/seferler?trip=${staleByVehicle.get(v.id)!.id}`}
                    title={t('dayBoard.staleTrip')}
                    data-testid="day-board-stale"
                  >
                    <StaleBadge />
                  </Link>
                )}
                {driverName && <span className="day-board-col-sub">{driverName}</span>}
                {driverRate !== undefined && driverRate !== null && (
                  <span className="day-board-col-sub" data-testid={`day-board-driver-rate-${v.id}`}>
                    {t('panel.shadowAssign.vehicleRate', { rate: rateText(driverRate) })}
                  </span>
                )}
                <span className="day-board-col-count">{cards.length}</span>
              </header>
              <ul className="day-board-list">
                {cards.length === 0 ? (
                  <li className="day-board-empty">{t('dayBoard.noOrders')}</li>
                ) : (
                  cards.map(renderCard)
                )}
              </ul>
            </section>
          );
        })}
        {otherOrders.length > 0 && (
          <section
            className="day-board-col"
            aria-label={t('dayBoard.otherVehicles')}
            data-testid="day-board-col"
          >
            <header className="day-board-col-head">
              <span className="day-board-col-title">{t('dayBoard.otherVehicles')}</span>
              <span className="day-board-col-count">{otherOrders.length}</span>
            </header>
            <ul className="day-board-list">{otherOrders.map(renderCard)}</ul>
          </section>
        )}
      </div>
    </div>
  );
}
